import {
  EMAIL_ATTACHMENT_LIMITS,
  TemplateChannel,
  TemplateContext,
  countTemplateContentCharacters,
  getTemplateVariableErrors,
  templateContentMaxLength,
  type CreateTemplateLibraryInput,
  type TemplateLibraryEntry,
  type TemplateLibraryLanguage,
  type UpdateTemplateLibraryInput,
} from '@fluenceur/types';
import { z } from 'zod';

export const TEMPLATE_NAME_MAX_LENGTH = 120;
export const TEMPLATE_LIBRARY_LANGUAGES = ['en', 'fr', 'it'] as const;

export const countTemplateCharacters = countTemplateContentCharacters;

/**
 * Client-side validation for the template form. The backend revalidates the
 * content against the same variable registry and shared checks as the API.
 */
export const templateFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'templates.form.errors.nameRequired')
      .max(TEMPLATE_NAME_MAX_LENGTH, 'templates.form.errors.nameTooLong'),
    channel: z.nativeEnum(TemplateChannel),
    context: z.nativeEnum(TemplateContext),
    language: z.enum(TEMPLATE_LIBRARY_LANGUAGES),
    // Mirror the backend validator (minLength 10) so the client blocks early and
    // surfaces the error inline instead of round-tripping to a 422.
    content: z.string().trim().min(10, 'templates.form.errors.contentTooShort'),
    contentLexicalJson: z.record(z.unknown()).nullable(),
    isDefault: z.boolean(),
    attachments: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          size: z.number().positive().max(EMAIL_ATTACHMENT_LIMITS.maxFileBytes),
          mimeType: z.string(),
        })
      )
      .max(EMAIL_ATTACHMENT_LIMITS.maxCount)
      .optional(),
  })
  .superRefine((values, ctx) => {
    const { invalidVariables, missingRequiredVariables } = getTemplateVariableErrors(
      values.channel,
      values.context,
      values.content
    );
    // Keep the API's order: unknown variables first, then required variables.
    if (invalidVariables.length || missingRequiredVariables.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['content'],
        message: invalidVariables.length
          ? 'templates.form.errors.invalidVariables'
          : 'templates.form.errors.missingRequiredVariables',
      });
    }
    if (values.attachments?.length && values.channel !== TemplateChannel.EMAIL) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attachments'],
        message: 'bulkEmail.modal.attachments.errors.emailOnly',
      });
    }
    if (
      (values.attachments ?? []).reduce((sum, file) => sum + file.size, 0) >
      EMAIL_ATTACHMENT_LIMITS.maxTotalBytes
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attachments'],
        message: 'bulkEmail.modal.attachments.errors.totalTooLarge',
      });
    }
    const maxLength = templateContentMaxLength(values.channel);
    if (countTemplateCharacters(values.content) > maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['content'],
        message: 'templates.form.errors.contentTooLong',
      });
    }
  });

export type TemplateFormValues = z.infer<typeof templateFormSchema>;

/**
 * Default values for a brand new template. Optionally seeded from a template
 * being duplicated: the copy inherits the pair, language and content but starts
 * as a non-default brand template with a fresh name.
 */
export const buildDefaultFormValues = (
  seed?: TemplateLibraryEntry,
  duplicateNameSuffix = ' (copy)'
): TemplateFormValues => {
  if (seed) {
    return {
      name: `${seed.name}${duplicateNameSuffix}`,
      channel: seed.channel,
      context: seed.context,
      language: seed.language,
      content: seed.content,
      contentLexicalJson: seed.contentLexicalJson,
      isDefault: false,
      ...(seed.attachments ? { attachments: seed.attachments } : {}),
    };
  }

  return {
    name: '',
    channel: TemplateChannel.EMAIL,
    context: TemplateContext.INVITATION,
    language: 'en',
    content: '',
    contentLexicalJson: null,
    isDefault: false,
  };
};

/** Map an existing entry to editable form values (edit mode). */
export const entryToFormValues = (entry: TemplateLibraryEntry): TemplateFormValues => ({
  name: entry.name,
  channel: entry.channel,
  context: entry.context,
  language: entry.language,
  content: entry.content,
  contentLexicalJson: entry.contentLexicalJson,
  isDefault: entry.isDefault,
  ...(entry.attachments ? { attachments: entry.attachments } : {}),
});

/** Build the create payload. Carries the locked pair + language. */
export const toCreateInput = (values: TemplateFormValues): CreateTemplateLibraryInput => ({
  name: values.name.trim(),
  channel: values.channel,
  context: values.context,
  language: values.language as TemplateLibraryLanguage,
  content: values.content,
  contentLexicalJson: values.contentLexicalJson,
  isDefault: values.isDefault,
  ...(values.attachments ? { attachmentIds: values.attachments.map((file) => file.id) } : {}),
});

/**
 * Build the update payload. Channel, context and language are immutable, so
 * they are intentionally omitted from the request body.
 */
export const toUpdateInput = (values: TemplateFormValues): UpdateTemplateLibraryInput => ({
  name: values.name.trim(),
  content: values.content,
  contentLexicalJson: values.contentLexicalJson,
  isDefault: values.isDefault,
  ...(values.attachments ? { attachmentIds: values.attachments.map((file) => file.id) } : {}),
});
